import { circulatingField, useApi, useCirculatingSupplyHistoric } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import HistoricGraphs from './HistoricGraphs';
import { useMemo, useState } from 'react';
import { Flex, DisplaySwitch } from '@blockanalitica/ui';

export default function HistoricTabs({ address }) {
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [activeDisplayOption, setActiveDisplayOption] =
    useState('total_corrected');
  const [data, error] = useApi({
    url: `/tokens/${address}/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const [supplyRows, supplyError] = useCirculatingSupplyHistoric(chartDaysAgo);
  const supplyField = data ? circulatingField(data.symbol) : undefined;

  // DAI and USDS take their supply from sky-api; the market cap is that supply
  // at the atlas price of the same day.
  const chartData = useMemo(() => {
    if (!data || !supplyField) {
      return data;
    }
    if (!supplyRows) {
      return undefined;
    }
    const supplyByDate = new Map(
      supplyRows.map((row) => [row.date, row[supplyField]])
    );
    return {
      ...data,
      data: data.data
        .filter((row) => supplyByDate.has(row.date))
        .map((row) => ({
          ...row,
          total_corrected: supplyByDate.get(row.date),
          market_cap_corrected:
            Number(supplyByDate.get(row.date)) * Number(row.price)
        }))
    };
  }, [data, supplyRows, supplyField]);

  const displayOptions = useMemo(
    () => [
      { key: 'total_corrected', value: 'Supply' },
      { key: 'market_cap_corrected', value: 'Market Cap' },
      { key: 'price', value: 'Price' }
    ],
    []
  );

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );

  if (error) {
    throw error;
  }
  if (supplyField && supplyError) {
    throw supplyError;
  }

  let content;
  if (chartData) {
    content = (
      <HistoricGraphs
        data={chartData}
        address={address}
        timeFilter={timeFilter}
        chartDaysAgo={chartDaysAgo}
        activeDisplayOption={activeDisplayOption}
      />
    );
  } else {
    content = <ChartSkeleton />;
  }

  return (
    <Flex flexDirection="column" gap="xs">
      <DisplaySwitch
        data={displayOptions}
        activeOption={activeDisplayOption}
        onChange={setActiveDisplayOption}
      />
      {content}
    </Flex>
  );
}
